/**
 * Format a phone number for display
 * @param phoneNumber The phone number to format
 * @returns A formatted phone number string
 */
export function formatPhoneNumber(phoneNumber: string): string {
  // Remove all non-numeric characters
  const cleaned = phoneNumber.replace(/\D/g, '');
  
  // Check if it's a US number (starts with 1 and has 11 digits)
  if (cleaned.startsWith('1') && cleaned.length === 11) {
    const countryCode = cleaned[0];
    const areaCode = cleaned.slice(1, 4);
    const firstPart = cleaned.slice(4, 7);
    const secondPart = cleaned.slice(7);
    return `+${countryCode} (${areaCode}) ${firstPart}-${secondPart}`;
  }
  
  // Check if it's a 10-digit number (US without country code)
  if (cleaned.length === 10) {
    const areaCode = cleaned.slice(0, 3);
    const firstPart = cleaned.slice(3, 6);
    const secondPart = cleaned.slice(6);
    return `(${areaCode}) ${firstPart}-${secondPart}`;
  }
  
  // For international numbers, just add + and space after country code
  if (cleaned.length > 10) {
    return `+${cleaned.slice(0, 2)} ${cleaned.slice(2)}`;
  }
  
  // Return original if no formatting rules match
  return phoneNumber;
}

/**
 * Validate if a phone number is in E.164 format
 * @param phoneNumber The phone number to validate
 * @returns true if valid, false otherwise
 */
export function isValidE164(phoneNumber: string): boolean {
  // E.164 format: +[country code][subscriber number]
  // Must start with +, followed by 1-3 digit country code, then up to 14 more digits
  const e164Regex = /^\+[1-9]\d{1,14}$/;
  return e164Regex.test(phoneNumber);
}

/**
 * Convert a phone number to E.164 format
 * @param phoneNumber The phone number to convert
 * @param defaultCountryCode The default country code to use if none is present
 * @returns The phone number in E.164 format
 */
export function toE164(phoneNumber: string, defaultCountryCode: string = '1'): string {
  // Remove all non-numeric characters except +
  const cleaned = phoneNumber.replace(/[^\d+]/g, '');
  
  // If already in E.164 format, return as is
  if (cleaned.startsWith('+')) {
    return cleaned;
  }
  
  // If 10 digits, assume US/Canada number
  if (cleaned.length === 10) {
    return `+${defaultCountryCode}${cleaned}`;
  }
  
  // If 11 digits and starts with 1, it's likely a US/Canada number with country code
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+${cleaned}`;
  }
  
  // For other cases, add the default country code
  return `+${defaultCountryCode}${cleaned}`;
}